import "./GeradorReelsPage.scss";

import { AnimatePresence, motion } from "framer-motion";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppInput from "../../../commons/inputs/AppInput";
import AppLoading from "../../../commons/AppLoading";
import CalendarInput from "../../../commons/inputs/CalendarInput";
import FloatingVideoButton from "../../../commons/buttons/FloatingVideoButton";
import HallAppToast from "../../../commons/HallAppToast";
import InstagramAPI from "../InstagramAPI";
import SelectBasicInput from "../../../commons/inputs/SelectBasicInput";
import { useNavigate } from "react-router-dom";

const CATEGORIES = [
  { label: "Promoção", value: "PROMO" },
  { label: "Engajamento", value: "ENGAGEMENT" },
  { label: "Antes e Depois", value: "BEFORE_AFTER" },
  { label: "Educacional (Dicas)", value: "TIP" },
  { label: "Prova Social", value: "SOCIAL_PROOF" },
  { label: "Serviço Específico", value: "SERVICE" },
];

const instaAPI = new InstagramAPI();

export default function GeradorReelsPage() {
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [usedAttempts, setUsedAttempts] = useState(0);
  const [instagramHandle, setInstagramHandle] = useState("@seuinstagram");
  const [missingInstagram, setMissingInstagram] = useState(false);
  const [limitExceeded, setLimitExceeded] = useState(false);

  const dailyLimit = 3;
  const [validUntilDate, setValidUntilDate] = useState(new Date());

  const hardLimitReached = usedAttempts >= dailyLimit || limitExceeded;
  const canGenerate = !!category && !loading && !hardLimitReached && !missingInstagram;

  useEffect(() => {
    getConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const getConfig = async () => {
    try {
      setLoadingPage(true);
      const response = await instaAPI.getReelsConfig();
      setUsedAttempts(response.data.usedAttempts);
      setInstagramHandle(response.data.instagram);
      setMissingInstagram(false);
    } catch (e) {
      const err = e?.response?.data;

      if (Array.isArray(err) && err[0]?.code === "INSTAGRAM_MANDATORY") {
        // Não possui Instagram cadastrado
        setMissingInstagram(true);
        return;
      } else {
        HallAppToast.genericError();
      }
    } finally {
      setLoadingPage(false);
    }
  };

  const initialValues = {
    services: "",
    discount: "",
    validUntil: null,
    theme: "",
    style: "",
    service: "",
    highlight: "",
    topic: "",
    level: "",
    review: "",
    differentials: "",
  };

  const validate = (values) => {
    const errors = {};

    if (!category) {
      errors.category = "Selecione um tipo de Reels.";
      return errors;
    }

    switch (category) {
      case "PROMO":
        if (!values.services) errors.services = "Informe os serviços da promoção.";
        if (!values.discount) errors.discount = "Informe o desconto.";
        if (!values.validUntil) errors.validUntil = "Informe a validade da promoção.";
        break;

      case "ENGAGEMENT":
        if (!values.theme) errors.theme = "Informe o tema do Reels.";
        if (!values.style) errors.style = "Selecione um estilo.";
        break;

      case "BEFORE_AFTER":
        if (!values.service) errors.service = "Informe o serviço.";
        if (!values.highlight) errors.highlight = "Informe o destaque do resultado.";
        break;

      case "TIP":
        if (!values.topic) errors.topic = "Informe o tema da dica.";
        if (!values.level) errors.level = "Selecione o nível.";
        break;

      case "SOCIAL_PROOF":
        if (!values.service) errors.service = "Informe o serviço.";
        if (!values.review) errors.review = "Informe um depoimento.";
        break;

      case "SERVICE":
        if (!values.service) errors.service = "Informe o serviço.";
        if (!values.differentials) errors.differentials = "Informe os diferenciais.";
        break;

      default:
        break;
    }

    return errors;
  };

  const handleSubmit = async (values) => {
    if (!category || hardLimitReached || missingInstagram) return;

    setLoading(true);
    setResult(null);

    const payload = {
      category,
      inputs: {
        ...values,
      },
    };

    try {
      const response = await instaAPI.generateReels(payload);
      setResult(response.data);

      // se gerou com sucesso, incrementa tentativa local (opcional)
      setUsedAttempts((prev) => prev + 1);
    } catch (e) {
      const err = e?.response?.data;

      if (Array.isArray(err) && err[0]?.code === "INSTAGRAM_LIMIT_EXCEEDED") {
        // trava front também
        setLimitExceeded(true);
        setUsedAttempts(dailyLimit);
        return;
      } else {
        HallAppToast.genericError();
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const renderFields = (
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    validUntilDate,
    setValidUntilDate
  ) => {
    switch (category) {
      case "PROMO":
        return (
          <motion.div
            key="promo"
            className="fields-box"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="services"
              label="Serviços na promoção"
              type="text"
              placeholder="Ex: Corte, escova, hidratação"
              onChange={handleChange}
              value={values.services}
            />

            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="discount"
              label="Desconto (%)"
              type="number"
              placeholder="Ex: 20"
              onChange={handleChange}
              value={values.discount}
            />

            <div className="calendar-field">
              <label className="calendar-label">Validade da promoção *</label>
              <CalendarInput
                selectionMode="single"
                values={validUntilDate}
                setDates={(date) => {
                  setValidUntilDate(date);
                  setFieldValue("validUntil", date);
                }}
                changeDates={(date) => {
                  setValidUntilDate(date);
                  setFieldValue("validUntil", date);
                }}
              />
              {errors.validUntil && (
                <div className="field-error">{errors.validUntil}</div>
              )}
            </div>
          </motion.div>
        );

      case "ENGAGEMENT":
        return (
          <motion.div
            key="engagement"
            className="fields-box"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="theme"
              label="Tema do Reels"
              type="text"
              placeholder="Ex: motivação, curiosidade, bastidor"
              onChange={handleChange}
              value={values.theme}
            />

            <SelectBasicInput
              errors={errors}
              touched={touched}
              name="style"
              label="Estilo"
              onChange={handleChange}
              value={values.style}
              options={[
                { key: "", label: "Selecione uma opção" },
                { key: "fun", label: "Divertido" },
                { key: "inspire", label: "Inspirador" },
                { key: "trend", label: "Trend" },
              ]}
            />
          </motion.div>
        );

      case "BEFORE_AFTER":
        return (
          <motion.div
            key="before_after"
            className="fields-box"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="service"
              label="Serviço"
              type="text"
              placeholder="Ex: Loiro iluminado, barba completa"
              onChange={handleChange}
              value={values.service}
            />

            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="highlight"
              label="Destaque do resultado"
              type="text"
              placeholder="Ex: brilho, alinhamento, volume"
              onChange={handleChange}
              value={values.highlight}
            />
          </motion.div>
        );

      case "TIP":
        return (
          <motion.div
            key="tip"
            className="fields-box"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="topic"
              label="Tema da dica"
              type="text"
              placeholder="Ex: Cuidados com loiro, pele oleosa, banho do pet"
              onChange={handleChange}
              value={values.topic}
            />

            <SelectBasicInput
              errors={errors}
              touched={touched}
              name="level"
              label="Nível"
              onChange={handleChange}
              value={values.level}
              options={[
                { key: "", label: "Selecione uma opção" },
                { key: "basic", label: "Básico" },
                { key: "mid", label: "Intermediário" },
                { key: "advanced", label: "Avançado" },
              ]}
            />
          </motion.div>
        );

      case "SOCIAL_PROOF":
        return (
          <motion.div
            key="social_proof"
            className="fields-box"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="service"
              label="Serviço"
              type="text"
              placeholder="Ex: Hidratação, design de sobrancelha"
              onChange={handleChange}
              value={values.service}
            />

            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="review"
              label="Depoimento"
              type="text"
              placeholder="Ex: Amei o resultado, atendimento perfeito!"
              onChange={handleChange}
              value={values.review}
            />
          </motion.div>
        );

      case "SERVICE":
        return (
          <motion.div
            key="service"
            className="fields-box"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="service"
              label="Serviço"
              type="text"
              placeholder="Ex: Corte bordado, banho e tosa premium"
              onChange={handleChange}
              value={values.service}
            />

            <AppInput
              errors={errors}
              touched={touched}
              isRequired
              name="differentials"
              label="Diferenciais"
              type="text"
              placeholder="Ex: técnica exclusiva, produtos veganos"
              onChange={handleChange}
              value={values.differentials}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (loadingPage) {
    return <AppLoading />;
  }

  if (missingInstagram) {
    return (
      <div className="reels-container">
        <div className="page-message page-message--error">
          <h2>Instagram não configurado</h2>
          <p>
            Para usar o <strong>Gerador de Reels</strong>, você precisa informar o
            Instagram do seu estabelecimento.
          </p>
          <p>
            Acesse o menu <strong>Meu Estabelecimento</strong>, edite o
            estabelecimento e preencha o campo <strong>Instagram</strong>.
          </p>
          <div className="page-message__actions">
            <AppButton
              type="button"
              name="Ir para Meus Estabelecimentos"
              onClick={() => navigate("/meus-estabelecimentos")}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmit}
      enableReinitialize={false}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => (
        <Form onSubmit={handleSubmit}>
          <motion.div
            className="reels-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="header">
              <h2 className="title">Gerador de Reels</h2>
              <p className="subtitle">
                Crie roteiros de Reels prontos para postar no Instagram do seu
                estabelecimento.
              </p>

              <div className="ig-chip">
                <span className="ig-label">Instagram do estabelecimento</span>
                <span className="ig-handle">{instagramHandle}</span>
              </div>

              <p className="limit-info">
                Você pode gerar até <strong>{dailyLimit}</strong> Reels por dia.{" "}
                Hoje você já usou <strong>{usedAttempts}</strong>.
              </p>

              {(usedAttempts >= dailyLimit || limitExceeded) && (
                <div className="limit-banner">
                  Você já atingiu o limite diário de geração de Reels. Tente
                  novamente amanhã.
                </div>
              )}

              {errors.category && (
                <div className="field-error">{errors.category}</div>
              )}

              <div className="actions-row">
                <button
                  type="button"
                  className="history-btn"
                  onClick={() => navigate("/instagram/gerador-reels/historico")}
                >
                  Ver histórico de Reels gerados
                </button>
              </div>
            </div>

            <div className="categories-grid">
              {CATEGORIES.map((c) => (
                <motion.button
                  type="button"
                  key={c.value}
                  whileTap={!hardLimitReached ? { scale: 0.96 } : {}}
                  className={`category-btn ${category === c.value ? "active" : ""} ${hardLimitReached ? "disabled" : ""
                    }`}
                  onClick={() => {
                    if (hardLimitReached) return;
                    setCategory(c.value);
                    setResult(null);
                  }}
                  disabled={hardLimitReached}
                >
                  {c.label}
                </motion.button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {category && (
                <motion.div
                  key={category}
                  className="fields-wrapper"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                >
                  {renderFields(
                    values,
                    errors,
                    touched,
                    handleChange,
                    setFieldValue,
                    validUntilDate,
                    setValidUntilDate
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <FloatingVideoButton isSecondFloatingButton={false} youtubeUrl={"https://www.youtube.com/embed/bWHKyfzqoz8"} />

            {category && (
              <motion.div
                whileTap={canGenerate ? { scale: 0.97 } : {}}
                className="generate-btn-wrapper"
              >
                <AppButton
                  type="submit"
                  width="100%"
                  name={loading ? "Gerando..." : "Gerar Reels"}
                  disabled={!canGenerate}
                />
              </motion.div>
            )}

            <AnimatePresence>
              {result && (
                <motion.div
                  className="result-box"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  <h3>{result.headline}</h3>

                  <div className="section">
                    <p className="label">Roteiro:</p>
                    <div className="content">
                      {(result.script || "")
                        .split("||BREAK||")
                        .map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                    </div>
                  </div>

                  <div className="section">
                    <p className="label">Legenda:</p>
                    <div className="content">
                      {(result.caption || "")
                        .split("||BREAK||")
                        .map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                    </div>
                  </div>

                  <div className="section">
                    <p className="label">Hashtags:</p>
                    <p className="content">
                      {Array.isArray(result.hashtags)
                        ? result.hashtags.join(" ")
                        : result.hashtags}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Form>
      )}


    </Formik>
  );
}
